import { useEffect, useState } from 'react';
import { useLoadingStore, LoadingStatus } from '@/features/loading/stores/useLoadingStore';
import { KEY_TO_RESOURCE, type ResourceKey } from '@/data/resources';

export type UseCustomLoaderProps = {
  key: ResourceKey;
  input: string | null | undefined;
  loader: any;
  onLoad?: (res: any) => void;
  onProgress?: (event: ProgressEvent<EventTarget>) => void;
  onError?: (err: Error) => void;
};

/**
 * Custom loader hook for Three.js loaders with dynamic inputs
 * Useful for textures or resources that need to be loaded based on runtime data
 *
 * @see https://github.com/pmndrs/react-three-fiber/blob/master/packages/fiber/src/core/hooks.tsx#L27
 * @param key - Unique key to track this resource in the loading store
 * @param input - URL or path to load
 * @param loader - Three.js loader instance (TextureLoader, GLTFLoader, etc.)
 * @param onLoad - Optional callback when loading succeeds
 * @param onProgress - Optional callback for loading progress
 * @param onError - Optional callback when loading fails
 * @returns The loaded resource or null
 */
export function useCustomLoader({
  key,
  input,
  loader,
  onLoad,
  onProgress,
  onError,
}: UseCustomLoaderProps): any | null {
  const [result, setResult] = useState<any | null>(null);
  const setStatus = useLoadingStore((s) => s.setStatus);

  useEffect(() => {
    // Validate key against resources - only track if it's a known resource
    const isValidKey = KEY_TO_RESOURCE.has(key);

    if (!input || !loader) {
      // If no input, reset state and set status to idle (only for valid keys)
      setResult(null);
      if (isValidKey) {
        setStatus(key, LoadingStatus.IDLE);
      }
      return;
    }

    const loadedItem = loader.load(
      input,
      (res: any) => {
        setResult(res);
        onLoad?.(res);
        if (isValidKey) {
          setStatus(key, LoadingStatus.LOADED);
        }
      },
      (event: ProgressEvent<EventTarget>) => {
        onProgress?.(event);
        if (isValidKey) {
          setStatus(key, LoadingStatus.LOADING);
        }
      },
      (err: Error) => {
        onError?.(err);
        if (isValidKey) {
          setStatus(key, LoadingStatus.ERROR, "failed to load ressource");
        }
      }
    );

    return () => {
      if (loadedItem?.dispose) loadedItem.dispose();
      setResult(null);
    };
  }, [key, input, loader, onLoad, onProgress, onError, setStatus]);

  return result;
}
